import { isSameDay, isWeekend } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { formatDayNumber, formatShortWeekday } from '@/utils/date-formatters';

import { WeekHeaderQuickAdd } from './week-header-quick-add';
import { WeekSlot } from './week-slot';

interface WeekGridHeaderProps {
  slots: WeekSlot[];
  canQuickAdd: boolean;
}

/**
 * Top row of the week grid: a "Project" label cell, 7 day-of-week cells
 * (with a hover-revealed "+" for new time entries), and a "Week total"
 * label cell.
 */
export function WeekGridHeader({ slots, canQuickAdd }: WeekGridHeaderProps) {
  const { t } = useTranslation('authenticated.timeTracking');

  return (
    <>
      <div className="flex items-center border-b bg-muted/40 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t('weekView.header.projectColumn')}
      </div>
      {slots.map(({ date }, i) => (
        <WeekGridHeaderDayCell key={i} date={date} canQuickAdd={canQuickAdd} />
      ))}
      <div className="flex items-center justify-end border-b border-l bg-muted/40 px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t('weekView.header.totalColumn')}
      </div>
    </>
  );
}

function WeekGridHeaderDayCell({ date, canQuickAdd }: { date: Date; canQuickAdd: boolean }) {
  const isToday = isSameDay(date, new Date());
  const weekend = isWeekend(date);
  const dayLabel = formatShortWeekday(date);
  const dateLabel = formatDayNumber(date);

  return (
    <div className="group relative flex flex-col items-center gap-0.5 border-b border-l bg-muted/40 px-2 py-2.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {dayLabel}
      </span>
      <span
        className={`text-sm font-semibold tabular-nums ${
          isToday
            ? 'rounded-full bg-foreground px-2 text-background'
            : weekend
              ? 'text-muted-foreground/70'
              : 'text-foreground'
        }`}
      >
        {dateLabel}
      </span>
      {canQuickAdd && <WeekHeaderQuickAdd date={date} />}
    </div>
  );
}
